import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Play, CheckCircle, XCircle, AlertCircle, 
  Send, MessageSquare, Wifi, WifiOff, RefreshCw,
  Activity, Database, Zap
} from 'lucide-react';
import flowExecutor from '../services/flowExecutor';
import whatsappService from '../services/whatsappService';
import dbService from '../services/dbService';
import toast from 'react-hot-toast';

const TestIntegration: React.FC = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [testPhone, setTestPhone] = useState('+393520663469');
  const [activeFlows, setActiveFlows] = useState<any[]>([]);
  const [recentMessages, setRecentMessages] = useState<any[]>([]);
  const [webhookUrl, setWebhookUrl] = useState('');

  useEffect(() => {
    checkConnection();
    loadActiveFlows();
    loadRecentMessages();
    
    // Imposta webhook URL
    setWebhookUrl(`https://caffesempione.algoretico.it/webhook`);
    
    const interval = setInterval(() => {
      loadRecentMessages();
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const checkConnection = async () => {
    const connected = await whatsappService.testConnection();
    setIsConnected(connected);
  };

  const loadActiveFlows = () => {
    const flows = dbService.getAllFlows().filter(f => f.isActive);
    setActiveFlows(flows);
  };

  const loadRecentMessages = () => {
    const messages = dbService.getMessages({ limit: 10 });
    setRecentMessages(messages);
  };

  const sendTestMessage = async () => {
    if (!testMessage || !testPhone) {
      toast.error('Inserisci numero e messaggio');
      return;
    }

    toast.loading('Invio messaggio di test...');
    
    const success = await whatsappService.sendMessage({
      to: testPhone,
      message: testMessage
    });
    
    if (success) {
      toast.success('Messaggio inviato con successo!');
      setTestMessage('');
      loadRecentMessages();
    } else {
      toast.error('Errore nell\'invio del messaggio');
    }
  };

  const simulateIncomingMessage = async () => {
    const simulatedMessage = {
      from: testPhone,
      message: testMessage || 'Test messaggio in arrivo',
      timestamp: new Date().toISOString()
    };

    // Simula la ricezione tramite webhook
    try {
      const response = await fetch('/api/webhook', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entry: [{
            changes: [{
              value: {
                messages: [{
                  from: simulatedMessage.from,
                  id: Math.random().toString(36).substr(2, 9),
                  timestamp: Date.now().toString(),
                  type: 'text',
                  text: {
                    body: simulatedMessage.message
                  }
                }]
              }
            }]
          }]
        })
      });

      if (response.ok) {
        toast.success('Messaggio simulato ricevuto!');
        
        // Attiva i flussi corrispondenti
        await flowExecutor.checkTriggers(simulatedMessage.message, simulatedMessage.from);
        
        loadRecentMessages();
      }
    } catch (error) {
      console.error('Errore simulazione:', error);
      toast.error('Errore nella simulazione');
    }
  };

  const testFlow = async (flow: any) => {
    toast.loading(`Test flow "${flow.name}"...`);
    
    const success = await flowExecutor.executeFlow(
      flow.id,
      flow.nodes,
      flow.edges,
      {
        type: 'manual_test',
        data: {
          message: 'Test manuale',
          userId: testPhone,
          timestamp: new Date().toISOString()
        }
      }
    );
    
    if (success) {
      toast.success('Flow eseguito con successo!');
    } else {
      toast.error('Errore nell\'esecuzione del flow');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
          Test Integrazione WhatsApp
        </h1>

        {/* Status Connessione */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-dark-surface rounded-xl p-6 shadow-sm mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Stato Connessione
            </h2>
            <button
              onClick={checkConnection}
              className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
          
          <div className="flex items-center gap-3">
            {isConnected ? (
              <>
                <Wifi className="w-6 h-6 text-green-500" />
                <span className="text-green-600 font-medium">Connesso</span>
              </>
            ) : (
              <>
                <WifiOff className="w-6 h-6 text-red-500" />
                <span className="text-red-600 font-medium">Non connesso</span>
              </>
            )}
          </div>
          
          <div className="mt-4 p-3 bg-gray-50 dark:bg-dark-bg rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Webhook URL: <code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">
                {webhookUrl}
              </code>
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sezione Test Messaggi */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white dark:bg-dark-surface rounded-xl p-6 shadow-sm"
          >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Test Messaggi
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Numero Telefono
                </label>
                <input
                  type="text"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="+393331234567"
                  className="w-full px-4 py-2 bg-gray-50 dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Messaggio
                </label>
                <textarea
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  placeholder="Inserisci il messaggio di test..."
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-50 dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green resize-none"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={sendTestMessage}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-whatsapp-green text-white rounded-lg hover:bg-whatsapp-dark transition-colors"
                >
                  <Send className="w-4 h-4" />
                  Invia
                </button>
                
                <button
                  onClick={simulateIncomingMessage}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <MessageSquare className="w-4 h-4" />
                  Simula Ricezione
                </button>
              </div>
            </div>
          </motion.div>

          {/* Flow Attivi */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white dark:bg-dark-surface rounded-xl p-6 shadow-sm"
          >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Flow Attivi ({activeFlows.length})
            </h2>
            
            {activeFlows.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                Nessun flow attivo
              </p>
            ) : (
              <div className="space-y-3">
                {activeFlows.map((flow) => (
                  <div
                    key={flow.id}
                    className="p-3 bg-gray-50 dark:bg-dark-bg rounded-lg flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <Zap className="w-5 h-5 text-emerald-500" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {flow.name}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {flow.nodes.length} nodi
                        </p>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => testFlow(flow)}
                      className="p-2 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
                    >
                      <Play className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>

        {/* Messaggi Recenti */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-dark-surface rounded-xl p-6 shadow-sm mt-6"
        >
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Messaggi Recenti
          </h2>
          
          {recentMessages.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">
              Nessun messaggio recente
            </p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {recentMessages.map((msg: any) => (
                <div
                  key={msg.id}
                  className={`p-3 rounded-lg ${
                    msg.type === 'sent' 
                      ? 'bg-whatsapp-light/20 ml-auto max-w-xs'
                      : 'bg-gray-100 dark:bg-dark-bg mr-auto max-w-xs'
                  }`}
                >
                  <p className="text-sm text-gray-900 dark:text-white">
                    {msg.content}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default TestIntegration;
